import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { LanguageContext, getTranslation } from '../LanguageContext';
import { FaMapMarkerAlt, FaArrowLeft, FaSpinner, FaImages, FaCalendarAlt, FaBuilding, FaSearchPlus, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './DepositStations.css';

const stationsTranslations = {
  en: {
    title: "Deposit Stations",
    subtitle: "Our deposit and reception stations",
    loading: "Loading stations...",
    noStations: "No deposit stations available yet",
    photos: "photos",
    viewDetails: "View Details",
    back: "Back",
    allStations: "All Deposit Stations",
    location: "Location",
    imageOf: "Image {current} of {total}"
  },
  fr: {
    title: "Nos émissions radio",
    subtitle: "Nos Nos émissions radio et d'accueil",
    loading: "Chargement des stations...",
    noStations: "Aucune station de dépôt disponible pour le moment",
    photos: "photos",
    viewDetails: "Voir les détails",
    back: "Retour",
    allStations: "Toutes les Nos émissions radio",
    location: "Emplacement",
    imageOf: "Image {current} de {total}"
  },
  ar: {
    title: "محطات إيداعية",
    subtitle: "محطات الإيداع والاستقبال الخاصة بنا",
    loading: "جاري تحميل المحطات...",
    noStations: "لا توجد محطات إيداعية متاحة حالياً",
    photos: "صور",
    viewDetails: "عرض التفاصيل",
    back: "العودة",
    allStations: "جميع المحطات الإيداعية",
    location: "الموقع",
    imageOf: "صورة {current} من {total}"
  }
};

const DepositStations = () => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const { language } = useContext(LanguageContext);
  const translate = (key) => getTranslation(stationsTranslations, language, key);
  const isRTL = language === 'ar';

  useEffect(() => {
    const fetchStations = async () => {
      try {
        setLoading(true);
        const res = await axios.get('http://localhost:4444/deposit-stations');
        setStations(res.data.data || []);
      } catch (error) {
        console.error('Error fetching deposit stations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStations();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const locale = language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const openImage = (station, image, index) => {
    setSelectedStation(station);
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
      setSelectedImage(selectedStation.images[currentImageIndex - 1]);
    }
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (selectedStation && currentImageIndex < selectedStation.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
      setSelectedImage(selectedStation.images[currentImageIndex + 1]);
    }
  };

  const closeModal = () => {
    setSelectedImage(null);
    setSelectedStation(null);
    setCurrentImageIndex(-1);
  };

  if (loading) {
    return (
      <div className="stations-page">
        <div className="stations-page-loading">
          <FaSpinner className="spin" />
          <p>{translate('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`stations-page ${isRTL ? 'rtl' : ''}`}>
      <div className="stations-page-hero">
        <div className="stations-page-hero-overlay"></div>
        <div className="stations-page-hero-content">
          <div className="stations-page-hero-nav">
            <Link to="/" className="stations-page-back-btn">
              <FaArrowLeft />
              <span>{translate('back')}</span>
            </Link>
          </div>
          <div className="stations-page-hero-icon">
            <FaBuilding />
          </div>
          <h1>{translate('title')}</h1>
          <p>{translate('subtitle')}</p>
        </div>
      </div>

      <div className="stations-page-content">
        <div className="stations-page-section-header">
          <h2>
            <FaMapMarkerAlt />
            {translate('allStations')}
          </h2>
          <span className="stations-page-count">{stations.length}</span>
        </div>

        {stations.length === 0 ? (
          <div className="stations-page-empty">
            <FaBuilding className="empty-icon" />
            <p>{translate('noStations')}</p>
          </div>
        ) : (
          <div className="stations-page-list">
            {stations.map((station) => (
              <div key={station._id} className="station-card">
                <div className="station-card-header">
                  <div className="station-card-info">
                    <h3>{station.title}</h3>
                    {station.location && (
                      <div className="station-location">
                        <FaMapMarkerAlt />
                        <span>{station.location}</span>
                      </div>
                    )}
                    {station.date && (
                      <div className="station-date">
                        <FaCalendarAlt />
                        <span>{formatDate(station.date)}</span>
                      </div>
                    )}
                  </div>
                  {station.images && station.images.length > 0 && (
                    <div className="station-photo-count">
                      <FaImages />
                      <span>{station.images.length} {translate('photos')}</span>
                    </div>
                  )}
                </div>

                {station.description && (
                  <p className="station-description">{station.description}</p>
                )}

                {station.images && station.images.length > 0 && (
                  <div className="station-gallery">
                    {station.images.map((image, index) => (
                      <div
                        key={index}
                        className="station-gallery-item"
                        onClick={() => openImage(station, image, index)}
                      >
                        <img src={image} alt={`${station.title} ${index + 1}`} loading="lazy" />
                        <div className="station-gallery-overlay">
                          <FaSearchPlus />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && selectedStation && (
        <div className="station-image-modal" onClick={closeModal}>
          <button className="station-modal-close" onClick={closeModal}>
            <FaTimes />
          </button>
          {currentImageIndex > 0 && (
            <button className="station-modal-nav station-modal-prev" onClick={handlePrev}>
              <FaChevronRight />
            </button>
          )}
          <div className="station-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="" />
            <div className="station-modal-info">
              <span>
                {translate('imageOf')
                  .replace('{current}', currentImageIndex + 1)
                  .replace('{total}', selectedStation.images.length)}
              </span>
            </div>
          </div>
          {currentImageIndex < selectedStation.images.length - 1 && (
            <button className="station-modal-nav station-modal-next" onClick={handleNext}>
              <FaChevronLeft />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DepositStations;
